import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Calendar, MapPin, Users, Clock } from "lucide-react";
import { Link } from "react-router-dom";

interface ActionLink {
  label: string;
  href: string;
  primary?: boolean;
}

export interface EventCardProps {
  id: string;
  title: string;
  description: string;
  date: string;
  time: string;
  location: string;
  image: string;
  attendees: number;
  maxAttendees?: number;
  price: string;
  category: string;
  featured?: boolean;
  actions?: ActionLink[];
  virtual_event?: string;
}

const EventCard = ({
  id,
  title,
  description,
  date,
  time,
  location,
  image,
  attendees,
  maxAttendees,
  price,
  category,
  featured = false,
  actions,
  virtual_event,
}: EventCardProps) => {
  const isSoldOut = maxAttendees && attendees >= maxAttendees;
  const percentageFilled = maxAttendees
    ? Math.min(100, Math.round((attendees / maxAttendees) * 100))
    : null;

  return (
    <div
      className={`bg-white rounded-lg overflow-hidden shadow-sm hover:shadow-md transition-all ${
        featured ? "border-2 border-accent" : "border border-gray-200"
      }`}
    >
      {featured && (
        <div className="bg-accent text-white text-xs font-medium text-center py-1">
          FEATURED EVENT
        </div>
      )}

      <div className="relative">
        <img src={image} alt={title} className="w-full h-48 object-cover" />
        <Badge className="absolute top-3 right-3 bg-white/90 text-cecure-800">
          {category}
        </Badge>
      </div>

      <div className="p-5">
        <div className="flex justify-between items-start">
          <h3 className="text-lg font-semibold text-cecure-900 line-clamp-2">
            {/* <Link to={`/event/${id}`} className="hover:text-accent">
              {title}
            </Link> */}
            <div className="hover:text-accent capitalize">{title}</div>
          </h3>
          <div className="text-right">
            <span className="text-md font-semibold text-cecure-800">
              {price}
            </span>
          </div>
        </div>

        <p className="mt-2 text-gray-600 text-sm line-clamp-2 capitalize">
          {description}
        </p>

        <div className="mt-4 space-y-2">
          <div className="flex items-center text-sm text-gray-600">
            <Calendar className="h-4 w-4 mr-2 text-gray-400" />
            <span>{date}</span>
          </div>

          <div className="flex items-center text-sm text-gray-600">
            <Clock className="h-4 w-4 mr-2 text-gray-400" />
            <span>{time}</span>
          </div>

          {virtual_event === "No" && (
            <div className="flex items-center text-sm text-gray-600">
              <MapPin className="h-4 w-4 mr-2 text-gray-400" />
              <span className="truncate">{location}</span>
            </div>
          )}
        </div>

        {percentageFilled !== null && (
          <div className="mt-4">
            <div className="flex justify-between text-xs mb-1">
              <span>{attendees} registered</span>
              <span>
                {isSoldOut ? "Sold out" : `${percentageFilled}% full`}
              </span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div
                className={`h-2 rounded-full ${
                  isSoldOut ? "bg-red-500" : "bg-green-500"
                }`}
                style={{ width: `${percentageFilled}%` }}
              ></div>
            </div>
          </div>
        )}

        <div className="mt-5">
          {actions ? (
            <div className="flex flex-col gap-2">
              {actions.map((action, index) => (
                <Link key={index} to={action.href}>
                  <Button
                    className={`w-full ${
                      action.primary ? "bg-cecure-700 hover:bg-cecure-800" : ""
                    }`}
                    variant={action.primary ? "default" : "outline"}
                  >
                    {action.label}
                  </Button>
                </Link>
              ))}
            </div>
          ) : (
            <Link to={`/event/${id}/register`}>
              <Button
                className={`w-full ${
                  isSoldOut
                    ? "bg-gray-300 hover:bg-gray-300 cursor-not-allowed"
                    : ""
                }`}
                disabled={isSoldOut}
              >
                {isSoldOut ? "Join Waitlist" : "Register Now"}
              </Button>
            </Link>
          )}
        </div>
      </div>
    </div>
  );
};

export default EventCard;
